
import Vue from 'vue';
import { oneOf } from '@core/utils/helpers';
import BaseButton from '@core/components/UI/BaseButton.vue';
import Dropdown from '@core/components/UI/dropdown/Dropdown.vue';
import MenuLightbox from '@root/common/components/base/MenuLightbox.vue';
import { stripTags } from '@core/filters/htmlFormater';

import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';

import { faCircle } from '@delfi.lt/fa-kit/icons/modules/classic/light';
import { faPeriod } from '@delfi.lt/fa-kit/icons/modules/classic/solid';

interface Option {
  name: string;
  value: unknown;
}

interface Data {
  selectedName: string | null;
  selectedValue: any;
  closeDropdown: { value: boolean; timestamp: string } | null;
  showLightbox: boolean;
}

interface Props {
  value: unknown;
  options: Option[];
  placeholder: string;
  size: string;
  lightbox: boolean;
}

interface Computed {
  classes: (string | { [x: string]: boolean })[];
}

interface Methods {
  getDefaultSelectName: (value: unknown) => void;
  handleSelect: () => void;
  select: (name: string, value: unknown) => void;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    BaseButton,
    Dropdown,
    MenuLightbox,
  },
  filters: {
    stripTags,
  },
  props: {
    /**
     * Selected value. Chooses value from options
     */
    value: {
      type: undefined,
      default: null,
      required: true,
    },
    /**
     * @values [{ name: 'name', value: 'value' }]
     */
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * @values 'auto', 'small', 'default'
     */
    size: {
      validator(value: string) {
        return oneOf(value, ['auto', 'small', 'default']);
      },
      type: String,
      default: () => 'default',
      required: false,
    },
    lightbox: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      selectedName: null,
      selectedValue: null,
      closeDropdown: null,
      showLightbox: false,
      faCircle,
      faPeriod,
    };
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(value) {
        this.getDefaultSelectName(value);
      },
    },
  },
  computed: {
    classes() {
      return [
        'form-select',
        {
          'form-select--lightbox': this.lightbox,
          [`form-select--size-${this.size}`]: this.size !== 'auto',
        },
      ];
    },
  },
  methods: {
    handleSelect() {
      this.showLightbox = !this.showLightbox;
      this.$emit('input', this.selectedValue);
    },
    select(name, value) {
      this.selectedName = name;
      this.selectedValue = isPlainObject(value) ? { ...(value as Record<string, unknown>) } : value;
      this.closeDropdown = {
        value: true,
        timestamp: String(new Date().getTime()),
      };

      if (!this.lightbox) {
        this.$emit('input', this.selectedValue);
      }
    },
    getDefaultSelectName(value) {
      let optionFound = false;
      for (const option of this.options) {
        if (isEqual(option.value, value)) {
          this.selectedName = option.name;
          optionFound = true;
          break;
        }
      }
      // Choose placeholder
      if (!optionFound) {
        this.selectedName = this.placeholder;
      }
    },
  },
  mounted() {
    if (this.value) {
      this.selectedValue = this.value;
    }
  },
});
